import { SearchForm } from "../Applicants/Applicants";
import styled from "@emotion/styled";
import PaymentTable from "./PaymentTable";
import { useMutation, useQuery } from "@tanstack/react-query";
import apiCall from "../../../utils/apiCall";
import { AxiosError } from "axios";
import { CircularProgress, Select } from "@mui/material";
import { DateFilter, FilterDropDown } from "../../../ui-components";
import { useMemo, useState } from "react";
import {
  ExNyRoute,
} from "../../../utils/allRouteId";
import { RouteResponse } from "../../../types/index";
import { FetchAllRoutes, reOrderFetchedData } from "../../../hooks/FetchData";
import { NotificationContext } from "../../../providers/notification";
import { useContext } from "react";
import { apiRequest, config } from "../../../api";
import fileDownload from "js-file-download";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

interface DisplayPaymentProps {
  setRouteName: React.Dispatch<React.SetStateAction<string>>;
}

const DisplayPayment = ({ setRouteName }: any) => {
  const [route, setRoute] = useState(Number(ExNyRoute));
  const [routeNameInt, setRouteNameInt] = useState("");
  const { showNotification } = useContext(NotificationContext);

  const [isDownloading, set_d_loading] = useState(false);

  const [date, setDate] = useState({
    start: "1909-08-13",
    end: "2100-08-13",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;

  const [paymentChannel, setPaymentChannel] = useState("");
  const [paymentAction, setPaymentAction] = useState("");
  const [membershipType, setMembershipType] = useState("1"); // New state for membershipType filter (defaults to "1")

  const { isLoading, data } = FetchAllRoutes();
  const allRoutes = isLoading
    ? []
    : reOrderFetchedData(data?.Data ?? []).map((r: any) => ({
        name: r.RouteName,
        val: r.RouteId,
      }));

  const getAllPaymentHistory = async () => {
    const params = new URLSearchParams({
      PageNumber: currentPage.toString(),
      PageSize: pageSize.toString(),
      routeId: route.toString(),
      startDate: date.start,
      endDate: date.end,
    });

    if (paymentChannel) params.append("paymentChannel", paymentChannel);
    if (paymentAction) params.append("paymentAction", paymentAction);
    if (membershipType) params.append("membershipType", membershipType); // Send membershipType directly to backend

    return apiCall().get(`Payment/GetAllPaymentMade?${params.toString()}`);
  };

  const paymentQuery = useQuery(
    [
      "get-All-Payments",
      route,
      date.start,
      date.end,
      currentPage,
      paymentChannel,
      paymentAction,
      membershipType, // Added to query key for refetching on change
    ],
    getAllPaymentHistory,
    {
      retry: 0,
      keepPreviousData: true,
    }
  );

  const apiData = paymentQuery.data?.data;

  const sortedItems = useMemo(() => {
    if (!apiData?.reportItems) return [];
    return [...apiData.reportItems].sort(
      (a: any, b: any) =>
        new Date(b.PaymentDate).getTime() -
        new Date(a.PaymentDate).getTime()
    );
  }, [apiData]);

  const tableData = {
    reportItems: sortedItems,
    PageCount: apiData?.PageCount ?? 1,
    TotalCount: apiData?.TotalCount ?? 0,
  };

  const handleRouteSwitch = (r: any) => {
    setCurrentPage(1);
    setRoute(r.val);
    setRouteName(r.name);
    setRouteNameInt(r.name);
  };

  const handleResetFilters = () => {
    setPaymentChannel("");
    setPaymentAction("");
    setMembershipType("1"); 
    setDate({
      start: "1909-08-13",
      end: "2100-08-13",
    });
    setCurrentPage(1);
  };

  const handleDownload = () => {
    set_d_loading(true);

    const params = new URLSearchParams();
    params.append("routeId", route.toString());

    if (date.start) {
      params.append("startDate", formatDate(date.start));
    }

    if (date.end) {
      params.append("endDate", formatDate(date.end));
    }

    if (paymentChannel) params.append("paymentChannel", paymentChannel);
    if (paymentAction) params.append("paymentAction", paymentAction);
    if (membershipType) params.append("membershipType", membershipType); // Include in download params

    apiRequest(
      `api/Payment/DownloadAllPaymentMade?${params.toString()}`,
      "POST",
      {}, 
      config,
      true
    )
      .then((data: any) => {
        if (!data?.Message) {
          fileDownload(data, `Payment-Report-${Date.now()}.xlsx`);
          showNotification({
            message: "File downloading...",
            type: "success",
          });
        } else {
          showNotification({
            message: data.Message || "No file to download",
            type: "info",
          });
        }
      })
      .catch(() => {
        showNotification({
          message: "Sorry, file cannot be downloaded",
          type: "error",
        });
      })
      .finally(() => set_d_loading(false));
  };

  return (
    <>
      <TopWrapper>
        <FilterDropDown
          onChange={handleRouteSwitch}
          options={allRoutes}
          text={routeNameInt || "Membership Route"}
          bg="#11643C"
          color="#fff"
        />

        <StyledSelect
          value={paymentChannel}
          onChange={(e) => {
            setPaymentChannel(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Channels</option>
          <option value="CyberPay">CyberPay</option>
          <option value="Interswitch">Interswitch</option>
          <option value="Deposit">Deposit</option>
          <option value="e-tranzact">E-tranzact</option>
        </StyledSelect>
          <StyledSelect
          value={paymentAction}
          onChange={(e) => {
            setPaymentAction(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All payment purpose</option>
          <option value="Subscription">Subscription Fee</option>
          <option value="Application">Application Fee</option>
          <option value="Acceptance">Acceptance Fee</option>
          <option value="training">Training Fee</option>
          <option value="induction">Induction Fee</option>
        </StyledSelect>

        <DateFilter
          style={{
            background: "#11643C",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: "4px",
            border: "none",
            width: "100px",
          }}
          onChange={setDate}
        />
        <button
          style={{
            background: "#11643C",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: "4px",
            border: "none",
            width: "100px",
          }}
          onClick={handleResetFilters}
        >
          Reset Filters
        </button>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          style={{
            background: "#11643C",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: "4px",
            border: "none",
            width: "100px",
          }}
        >
          {isDownloading ? "Downloading..." : "Download Payments"}
        </button>
      </TopWrapper>

      {!paymentQuery.isLoading && (
        <div style={{ margin: "10px 0", fontSize: "0.9rem" }}>
          Showing {(currentPage - 1) * pageSize + 1} –{" "}
          {Math.min(currentPage * pageSize, tableData.TotalCount)} of{" "}
          {tableData.TotalCount} records
        </div>
      )}

      {paymentQuery.isLoading ? (
        <CircularProgress sx={{ display: "block", margin: "2rem auto" }} />
      ) : (
        <PaymentTable
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          data={tableData}
        />
      )}
    </>
  );
};

export default DisplayPayment;

const TopWrapper = styled("div")`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 10px 10px 48px 0px;
`;

const StyledSelect = styled("select")`
  padding: 8px 12px;
  border: 1px solid #1d3923;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  background-color: #96b1a4;
  color:"#fff";

  &:focus {
    outline: none;
    border-color: #11643c;
  }
`;

const StyledError = styled("p")`
  color: red;
  font-size: 1.4rem;
  display: flex;
`;
